import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Header } from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Flame, TrendingUp, AlertTriangle, ThumbsDown, Search, Phone, Mail, Target, DollarSign,
  Clock, RefreshCw, ChevronRight, FileText, CheckCircle2, Send, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { EmptyState } from '../components/EmptyState';
import { noahToast } from '../lib/noahToast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const scoreColor = (s) =>
  s >= 85 ? 'text-emerald-700 dark:text-emerald-400' :
  s >= 70 ? 'text-amber-700 dark:text-amber-400' :
  s >= 50 ? 'text-amber-700 dark:text-amber-400' : 'text-red-600 dark:text-red-400';

const urgencyColor = {
  high: 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30',
  medium: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30',
  low: 'bg-zinc-500/15 text-zinc-500 border-zinc-500/30',
};

const sentimentEmoji = { positive: '😊', neutral: '😐', negative: '😟' };

export default function DealsCenter() {
  const [data, setData] = useState({ rows: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [filters, setFilters] = useState({ min_score: 0, urgency: '', sentiment: '', q: '' });
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const params = {};
    if (filters.min_score) params.min_score = filters.min_score;
    if (filters.urgency) params.urgency = filters.urgency;
    if (filters.sentiment) params.sentiment = filters.sentiment;
    axios.get(`${API}/deals/pipeline`, { params })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) toast.error('No se pudo cargar el pipeline'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reloadKey, filters.min_score, filters.urgency, filters.sentiment]);

  const filteredRows = (data.rows || []).filter(r => {
    if (!filters.q) return true;
    const q = filters.q.toLowerCase();
    return (r.name || '').toLowerCase().includes(q) ||
           (r.intelligence?.product_interest || '').toLowerCase().includes(q) ||
           (r.phone || '').toLowerCase().includes(q) ||
           (r.email || '').toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen pb-28" data-testid="deals-center-page">
      <Header title="Gestión Noah — Ventas & Soporte" subtitle="Datos recolectados por Noah listos para cerrar" />
      <div className="container mx-auto px-4 py-6 max-w-7xl space-y-4">

        {/* Stats hero */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={<TrendingUp />} label="Total con datos" value={data.stats?.total || 0} color="text-brand-deep dark:text-brand-green" bg="bg-brand-green/10" />
          <StatCard icon={<Flame />} label="Hot leads (≥70%)" value={data.stats?.hot_leads || 0} color="text-amber-700 dark:text-amber-400" bg="bg-orange-500/10" testid="stat-hot" />
          <StatCard icon={<AlertTriangle />} label="Urgencia alta" value={data.stats?.high_urgency || 0} color="text-red-600 dark:text-red-400" bg="bg-red-500/10" />
          <StatCard icon={<ThumbsDown />} label="Sentimiento negativo" value={data.stats?.negative_sentiment || 0} color="text-zinc-500" bg="bg-zinc-500/10" />
        </div>

        {/* Filters */}
        <Card className="glass-card">
          <CardContent className="p-4 flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Buscar por nombre, producto, tel, email..."
                value={filters.q} onChange={e => setFilters({...filters, q: e.target.value})}
                className="pl-8" data-testid="deals-search" />
            </div>
            <select value={filters.min_score} onChange={e => setFilters({...filters, min_score: parseInt(e.target.value)})}
              className="px-3 py-2 rounded-md border bg-background text-sm" data-testid="deals-min-score">
              <option value={0}>Score: cualquiera</option>
              <option value={50}>Score ≥ 50</option>
              <option value={70}>Score ≥ 70 (hot)</option>
              <option value={85}>Score ≥ 85 (muy caliente)</option>
            </select>
            <select value={filters.urgency} onChange={e => setFilters({...filters, urgency: e.target.value})}
              className="px-3 py-2 rounded-md border bg-background text-sm" data-testid="deals-urgency">
              <option value="">Urgencia: cualquiera</option>
              <option value="high">Alta</option>
              <option value="medium">Media</option>
              <option value="low">Baja</option>
            </select>
            <select value={filters.sentiment} onChange={e => setFilters({...filters, sentiment: e.target.value})}
              className="px-3 py-2 rounded-md border bg-background text-sm" data-testid="deals-sentiment">
              <option value="">Sentimiento: todos</option>
              <option value="positive">Positivo</option>
              <option value="neutral">Neutral</option>
              <option value="negative">Negativo</option>
            </select>
            <Button variant="outline" size="icon" onClick={() => setReloadKey(k => k + 1)} data-testid="deals-refresh">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </CardContent>
        </Card>

        {/* Deals list */}
        <div className="space-y-2">
          {loading && <p className="text-sm text-muted-foreground p-6 text-center">Cargando datos de Noah...</p>}
          {!loading && filteredRows.length === 0 && (
            <Card className="glass-card">
              <CardContent className="p-10 text-center">
                <EmptyState
                  mood="thinking"
                  title="Aún no hay datos recolectados por Noah"
                  description="Cuando Noah conteste chats, la información del cliente (presupuesto, objeciones, urgencia) se resumirá aquí automáticamente."
                  testId="deals-empty"
                />
              </CardContent>
            </Card>
          )}
          {filteredRows.map(r => (
            <DealRow key={r.customer_id} row={r} expanded={expanded === r.customer_id}
              onToggle={() => setExpanded(expanded === r.customer_id ? null : r.customer_id)}
              onRefresh={() => setReloadKey(k => k + 1)} />
          ))}
        </div>
      </div>
    </div>
  );
}

const StatCard = ({ icon, label, value, color, bg, testid }) => (
  <Card className="glass-card" data-testid={testid}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${bg} ${color}`}>{icon}</div>
      <div>
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-[11px] text-muted-foreground">{label}</p>
      </div>
    </CardContent>
  </Card>
);

const DealRow = ({ row, expanded, onToggle, onRefresh }) => {
  const intel = row.intelligence || {};
  const score = intel.buy_intent_score || 0;
  const [busy, setBusy] = useState(null); // 'quote' | 'won' | 'followup' | null
  const [quotePreview, setQuotePreview] = useState(null);
  const [quotesHistory, setQuotesHistory] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  // Load quotes history when expanded
  React.useEffect(() => {
    if (!expanded || historyLoaded) return;
    axios.get(`${API}/quotes?customer_id=${row.customer_id}`)
      .then(({ data }) => setQuotesHistory(data || []))
      .catch(() => {})
      .finally(() => setHistoryLoaded(true));
  }, [expanded, historyLoaded, row.customer_id]);

  const doAction = async (kind, endpoint, successMsg) => {
    setBusy(kind);
    try {
      const { data } = await axios.post(`${API}${endpoint}`);
      if (kind === 'won') {
        let msg = `${row.customer_name || 'El cliente'} pasó a Ganado`;
        try {
          const { data: st } = await axios.get(`${API}/deals/streak`);
          if (st?.streak >= 2) {
            msg = `Racha de ${st.streak} cierres seguidos · $${Number(st.streak_value || 0).toLocaleString()}`;
          }
        } catch { /* la racha es un extra */ }
        noahToast('happy', '¡Trato ganado! 🎉', msg);
      } else {
        toast.success(successMsg);
      }
      if (kind === 'quote' && data?.content) {
        setQuotePreview(data.content);
        setQuotesHistory(prev => [data, ...prev]);
      }
      if (kind === 'won') onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setBusy(null);
    }
  };

  const updateQuoteStatus = async (quoteId, newStatus) => {
    try {
      await axios.put(`${API}/quotes/${quoteId}/status?status=${newStatus}`);
      toast.success(`Cotización marcada como ${newStatus}`);
      setQuotesHistory(prev => prev.map(q => q.id === quoteId ? { ...q, status: newStatus } : q));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  return (
    <Card className="glass-card overflow-hidden" data-testid={`deal-row-${row.customer_id}`}>
      <CardContent className="p-0">
        <button onClick={onToggle} className="w-full p-4 flex items-center gap-4 hover:bg-muted/30 transition-colors text-left" data-testid={`deal-toggle-${row.customer_id}`}>
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-semibold flex-shrink-0">
            {(row.name || '?').charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium">{row.name}</span>
              <Badge variant="outline" className="text-[10px]">{row.status}</Badge>
              <span className="text-xs text-muted-foreground">{sentimentEmoji[intel.sentiment] || ''} {intel.sentiment || ''}</span>
              {intel.urgency && <Badge variant="outline" className={`text-[10px] ${urgencyColor[intel.urgency] || ''}`}>{intel.urgency}</Badge>}
            </div>
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {intel.product_interest || intel.summary || '—'}
            </p>
          </div>
          <div className="flex flex-col items-end gap-0.5">
            <span className={`text-2xl font-bold ${scoreColor(score)}`}>{score}%</span>
            <span className="text-[10px] text-muted-foreground">intención</span>
          </div>
          <ChevronRight className={`w-5 h-5 text-muted-foreground transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </button>
        {expanded && (
          <div className="px-4 pb-4 pt-2 border-t border-white/10 space-y-3 text-sm" data-testid={`deal-detail-${row.customer_id}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <InfoField icon={<Phone />} label="Teléfono" value={row.phone} />
              <InfoField icon={<Mail />} label="Email" value={row.email} />
              <InfoField icon={<Target />} label="Interés" value={intel.product_interest} />
              <InfoField icon={<DollarSign />} label="Presupuesto" value={intel.budget} />
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground uppercase mb-1">Objeciones</p>
              <div className="flex flex-wrap gap-1">
                {(intel.objections || []).length === 0 && <span className="text-xs text-muted-foreground italic">Ninguna detectada</span>}
                {(intel.objections || []).map(o => (
                  <span key={o} className="text-[11px] px-2 py-0.5 rounded bg-amber-500/10 text-amber-600 border border-amber-500/20">{o}</span>
                ))}
              </div>
            </div>
            <div className="rounded-lg bg-violet-500/10 border border-violet-500/20 p-3">
              <p className="text-[11px] font-semibold text-brand-deep dark:text-brand-deep dark:text-brand-green uppercase mb-1">🎯 Próxima acción sugerida</p>
              <p className="text-sm">{intel.next_action || '—'}</p>
            </div>
            {intel.summary && (
              <div className="text-xs text-muted-foreground italic border-l-2 border-brand-green/30 pl-3">
                {intel.summary}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-white/10">
              <Button
                size="sm"
                onClick={() => doAction('quote', `/deals/${row.customer_id}/quote`, 'Cotización generada por Noah')}
                disabled={busy === 'quote'}
                className="bg-brand-green hover:bg-brand-green text-white"
                data-testid={`action-quote-${row.customer_id}`}
              >
                {busy === 'quote' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
                Generar cotización
              </Button>
              <Button
                size="sm"
                onClick={() => doAction('followup', `/deals/${row.customer_id}/follow-up`, 'Seguimiento enviado en el chat')}
                disabled={busy === 'followup'}
                className="bg-violet-600 hover:bg-violet-700 text-white"
                data-testid={`action-followup-${row.customer_id}`}
              >
                {busy === 'followup' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                Enviar seguimiento
              </Button>
              <Button
                size="sm"
                onClick={() => doAction('won', `/deals/${row.customer_id}/close-won`, '¡Trato ganado!')}
                disabled={busy === 'won' || row.status === 'closed_won'}
                className="bg-emerald-600 hover:bg-emerald-700 text-white ml-auto"
                data-testid={`action-won-${row.customer_id}`}
              >
                {busy === 'won' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                {row.status === 'closed_won' ? 'Ganado' : 'Marcar ganado'}
              </Button>
            </div>

            {quotePreview && (
              <div className="mt-3 p-3 rounded-lg bg-brand-green/10 border border-brand-green/20" data-testid={`quote-preview-${row.customer_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[11px] font-semibold text-brand-deep dark:text-brand-green uppercase">Cotización de Noah (nueva)</p>
                  <button onClick={() => setQuotePreview(null)} className="text-muted-foreground hover:text-foreground text-lg">×</button>
                </div>
                <pre className="text-xs whitespace-pre-wrap font-sans">{quotePreview}</pre>
              </div>
            )}

            {/* Quotes history */}
            {historyLoaded && quotesHistory.length > 0 && (
              <div className="pt-2 border-t border-white/10" data-testid={`quotes-history-${row.customer_id}`}>
                <p className="text-[11px] font-semibold text-muted-foreground uppercase mb-2">
                  📄 Historial de cotizaciones ({quotesHistory.length})
                </p>
                <div className="space-y-2">
                  {quotesHistory.map(q => (
                    <QuoteHistoryItem key={q.id} quote={q} onUpdateStatus={updateQuoteStatus} />
                  ))}
                </div>
              </div>
            )}

            <div className="text-[10px] text-muted-foreground flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Actualizado: {row.updated_at ? new Date(row.updated_at).toLocaleString('es') : '—'}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const InfoField = ({ icon, label, value }) => (
  <div className="flex items-start gap-2">
    <div className="w-4 h-4 mt-0.5 text-muted-foreground">{icon}</div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] text-muted-foreground uppercase">{label}</p>
      <p className="text-sm truncate">{value || '—'}</p>
    </div>
  </div>
);

const quoteStatusColor = {
  draft: 'bg-zinc-500/15 text-zinc-500 border-zinc-500/30',
  pending: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30',
  accepted: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30',
  rejected: 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30',
  expired: 'bg-orange-500/15 text-amber-700 dark:text-amber-400 border-orange-500/30',
};

const QuoteHistoryItem = ({ quote, onUpdateStatus }) => {
  const [expanded, setExpanded] = useState(false);
  const dt = quote.created_at ? new Date(quote.created_at).toLocaleString('es', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
  return (
    <div className="rounded-lg bg-muted/30 border border-white/5 overflow-hidden" data-testid={`quote-item-${quote.id}`}>
      <div className="flex items-center gap-2 p-2 text-xs">
        <FileText className="w-3.5 h-3.5 text-brand-deep dark:text-brand-green flex-shrink-0" />
        <span className="text-muted-foreground">{dt}</span>
        <Badge variant="outline" className={`text-[10px] ${quoteStatusColor[quote.status] || ''}`}>{quote.status || 'draft'}</Badge>
        {quote.generated_by === 'noah' && <Badge variant="outline" className="text-[10px] bg-violet-500/10 text-brand-deep dark:text-brand-deep dark:text-brand-green border-violet-500/20">Noah</Badge>}
        <button onClick={() => setExpanded(!expanded)} className="ml-auto text-[10px] text-primary hover:underline" data-testid={`quote-toggle-${quote.id}`}>
          {expanded ? 'Ocultar' : 'Ver contenido'}
        </button>
      </div>
      {expanded && (
        <div className="p-3 border-t border-white/10">
          <pre className="text-[11px] whitespace-pre-wrap font-sans max-h-64 overflow-y-auto">{quote.content || '(sin contenido)'}</pre>
          <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-white/5">
            {['pending', 'accepted', 'rejected', 'expired'].map(st => (
              <button
                key={st}
                onClick={() => onUpdateStatus(quote.id, st)}
                disabled={quote.status === st}
                className={`text-[10px] px-2 py-1 rounded border ${quote.status === st ? 'opacity-40' : 'hover:bg-muted'} ${quoteStatusColor[st]}`}
                data-testid={`quote-status-${quote.id}-${st}`}
              >
                {st}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
